// Responsibility: Helper utilities for admin preforge workflows.
// Scope: Runtime helper logic only; no UI ownership.
export function slugify(text: string): string {
  return String(text ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
}

export function titleCase(text: string): string {
  return String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function buildPreforgePairingLabel(offer: any): string {
  if (!offer) return 'No reality loaded'
  if (offer.kind === 'world') {
    return offer.vesselCode ? 'World-only shell with vessel reference' : 'World-only shell'
  }
  const hasVessel = Boolean(String(offer.vesselCode ?? '').trim())
  const hasStarter = Boolean(String(offer.starterCode ?? '').trim())
  if (hasVessel && hasStarter) return 'Reality pair ready'
  if (hasVessel || hasStarter) return 'Reality partially paired'
  return 'Reality missing pair data'
}

export function formatPreforgeRecipeSummary(recipe: any): string {
  if (!recipe) return 'Legacy capture without recipe'
  const source = recipe.sourceWorld.kind === 'liveRun' || recipe.sourceWorld.kind === 'savedWorld'
    ? `${recipe.sourceWorld.kind}:${recipe.sourceWorld.saveId}`
    : recipe.sourceWorld.kind === 'templateWorld'
      ? `template:${recipe.sourceWorld.templateId}`
      : `preset:${recipe.sourceWorld.presetId}`
  return `${source} · ${recipe.scale} · ${recipe.density.rooms} rooms · ${recipe.density.npcs} NPCs · ${recipe.outputMode}`
}

export function formatPreforgeDraftSummary(draft: any): string {
  if (!draft) return 'No draft expansion yet'
  const seed = draft.seed.code ? `${draft.seed.mode}:${draft.seed.code}` : draft.seed.mode
  const vessel = draft.vessel.code ? `${draft.vessel.mode}:${draft.vessel.code}` : draft.vessel.mode
  const brief = draft.brief || draft.seed.summary || draft.vessel.bio || 'Draft ready'
  return `${seed} · ${vessel} · ${brief.slice(0, 90)}`
}

export function formatPreforgeReportSummary(report: any): string {
  if (!report) return 'No forge report yet'
  const measure = report.measures[0]
  const headline = report.title || 'Forge report'
  return `${headline} · ${measure ? `${measure.label} ${measure.actual}/${measure.requested}` : 'Measured'}`
}

export function sourceWorldId(recipe: any): string {
  const source = recipe?.sourceWorld
  if (!source) return ''
  if (source.kind === 'liveRun' || source.kind === 'savedWorld') return source.saveId
  if (source.kind === 'templateWorld') return source.templateId
  return source.presetId
}

export function providerLabel(providerId: string): string {
  switch (providerId) {
    case 'vertex-managed': return 'Managed Cloud AI'
    case 'gemini': return 'Google Gemini'
    case 'openai': return 'OpenAI'
    case 'anthropic': return 'Anthropic Claude'
    case 'groq': return 'Groq'
    case 'ollama': return 'Ollama (Local)'
    case 'auto': return 'Auto'
    default: return String(providerId || 'unknown')
  }
}

export function formatPreforgeRouteDetails(error: any): any {
  if (!error || typeof error.diagnostics !== 'object') return null
  const d = error.diagnostics || {}
  const providerLines = Array.isArray(d.providers)
    ? d.providers.map((entry: any) => `${entry.label}: ${entry.enabled ? 'enabled' : 'disabled'} · ${entry.available ? 'online' : 'offline'} · ${entry.selectedModel || 'unset'}`)
    : []
  return {
    title: error.message || 'AI Route Failed',
    copy: 'The preforge orchestrator could not reach a capable AI provider. Verify your AI settings and local hardware status.',
    actionLabel: 'OPEN AI SETTINGS',
    details: providerLines,
  }
}
